/** @jsx jsx */
import { List, Modal, MultiButton, Spacer, TextGroupMedium } from "@cd2/components"
import { useSkeem } from "../../../providers/SkeemProvider"
import { H3, P } from "@cd2/text"
import { TextBlock } from "@cd2/text/build/es6"
import { jsx } from "@emotion/core"
import React, { useState } from "react"
import { useHistory } from "react-router-dom"

import { useAsyncCallback } from "../../../hooks/useAsyncCallback"
import { useAsyncEffect } from "../../../hooks/useAsyncEffect"
import { useArchiveUserDialog } from "../../../hooks/useArchiveUserDialog"
import { useFetchMe } from "../../../hooks/useFetchMe"
import { useDialogueConfirmation2 } from "../../../hooks/useDialogConfirmation"
import { useManagedConfirmation } from "../../../hooks/useManagedConfirmation"
import { useSiteAlerts } from "../../../hooks/useSiteAlerts"
import { IRecord } from "../../../types/schema"
import { useApi } from "../../../providers/ApiProvider"
import { useEvents } from "../../../utils/useEvents"
import { NewReminderForm } from "../../../forms/NewReminderForm"
import { useDrawConfirmation } from "../../../hooks/useDrawConfirmation"
import { restoreEmail } from "../../../utils/email"
import { diffRecordsForUpdateEvent } from "../../../utils/eventUtils"
import { isPayrollUser } from "../../../utils/isPayrollUser"
import { assert } from "@cd2/tools"
import { CGButton } from "../../../interfaceParts/CGButton"
import { LoadingFrame } from "../UserResultsShowPage/AssessmentDetailsTab"
import { eRoutes } from "../../../routes"

export function useCreateTaskDialog() {
  return useDrawConfirmation<
    boolean,
    { user: IRecord<"skeem_users">; preventChangingUser?: boolean }
  >({
    portal: true,
    Component({ dialogData, onResolve }) {
      return (
        <div>
          <H3>Create New Task</H3>
          <NewReminderForm
            initialData={{ userId: dialogData.user.id, body: "" }}
            onAfterSubmit={() => {
              onResolve(true)
            }}
            additionalButtons={[
              { children: "Cancel", buttonStyle: "text", onClick: () => onResolve(null) },
            ]}
            showUserInput={!dialogData.preventChangingUser}
            noMicrosoft
          />
        </div>
      )
    },
  })
}

type UserManageUserActionsRecord = IRecord<
  "skeem_users",
  | "isPayrollUser"
  | "useNewLoginLogic"
  | "payrollNumber"
  | "skeem_email"
  | "authMethod"
  | "contactEmail"
  | "isInvited"
  | "isLeaver"
  | "isOwner"
  | "isAnonymous"
> & {
  employee: IRecord<"employees"> | null
  company: IRecord<"companies"> & {
    users: IRecord<"skeem_users", "isOwner">[]
  }
}

export function UserManageUserActionsTab({
  userId,
  refetch,
}: {
  userId: string
  refetch: () => void | Promise<void>
}) {
  const [user, setUser] = useState<UserManageUserActionsRecord | null>(null)

  useAsyncEffect(async () => {
    setUser(null)
    const record = await window.skeem.fetch2<UserManageUserActionsRecord | null>({
      first: {
        name: "skeem_users",
        matches: { id: userId },
        attributes: [
          "isPayrollUser",
          "useNewLoginLogic",
          "payrollNumber",
          "skeem_email",
          "authMethod",
          "contactEmail",
          "isInvited",
          "isLeaver",
          "isOwner",
          "isAnonymous",
          { name: "employee", attributes: [] },
          {
            name: "company",
            attributes: [{ name: "users", attributes: ["isOwner"] }],
          },
        ],
      },
    })
    assert(record, "User record not found")
    setUser(record)
  }, [userId])

  if (!user) return <LoadingFrame />
  return <UserManageUserActionsTabInner user={user} refetch={refetch} />
}

function UserManageUserActionsTabInner({
  user,
  refetch,
}: {
  user: UserManageUserActionsRecord
  refetch: () => void | Promise<void>
}) {
  const me = useFetchMe()
  const skeem = useSkeem()
  const events = useEvents()
  const alerts = useSiteAlerts()
  const api = useApi()
  const history = useHistory()

  const openArchiveDialog = useArchiveUserDialog()

  const [
    getAssignOwnerConfirmation,
    { showDialog: showAssignOwnerDialog, onResolve: onAssignOwnerResolve },
  ] = useManagedConfirmation<any, boolean>()

  const confirmUserReturned = useDialogueConfirmation2<boolean>({
    Component: ({ onResolve, registerOutsideClick }) => {
      registerOutsideClick(() => onResolve(false))
      return (
        <div>
          <TextGroupMedium
            heading="Mark user as has returned?"
            body={<P>Are you sure you want to mark user as has returned?</P>}
          />
          <Spacer />
          <MultiButton
            buttons={[
              { children: "Confirm", onClick: () => onResolve(true) },
              { children: "Cancel", buttonColor: "white", onClick: () => onResolve(false) },
            ]}
          />
        </div>
      )
    },
  })
  const [isActioning, setIsActioning] = useState(false)

  let backPath: string = eRoutes["/manager/users"]
  if (me?.isAdmin) backPath = `/companies/${user.company.id}`

  async function handleSendPasswordResetEmail() {
    setIsActioning(true)
    try {
      await skeem.generatePasswordResetToken({
        email: isPayrollUser(user) ? user.contactEmail : user.skeem_email,
      })
      if (user.isInvited) alerts.inviteResentAlert()
      else alerts.passwordResetSentAlert()
    } catch (e) {
      console.error(e)
      alerts.genericError()
    } finally {
      setIsActioning(false)
    }
  }

  async function toggleUserHasLeft() {
    const newState = !user.isLeaver

    const company = await skeem.fetch2<IRecord<"companies", "userLimit"> & { userCount: number }>({
      first: {
        name: "companies",
        attributes: [
          "userLimit",
          {
            as: "userCount",
            value: {
              count: {
                attr: {
                  name: "users",
                  filter: {
                    matches: { archived: false, isAnonymous: false, isLeaver: false },
                  },
                },
              },
            },
          },
        ],
        filter: {
          exists: {
            path: [{ name: "users", filter: { matches: { id: user.id } } }],
          },
        },
      },
    })

    if (newState) {
      const handle = await openArchiveDialog({ users: [user.id] })
      if (!handle) return
      try {
        await api.updateCompanySubscription(company.id)
        await Promise.resolve(refetch())
        alerts.genericSuccess("User marked as has left")
      } catch (e) {
        console.error(e)
        alerts.genericError("Error updating user")
      }
      return
    }

    if (company.userLimit && company.userCount + 1 > company.userLimit) {
      alerts.genericError(
        "You have reached the maximum user count for your company. Remove a user to restore this one.",
      )
      return
    }

    if (!(await confirmUserReturned(undefined))) return

    try {
      const update: any = {
        id: user.id,
        isLeaver: false,
        archived: false,
        skeem_email: restoreEmail(user.skeem_email),
      }
      if (user.employee) {
        update.employee = { update: { id: user.employee.id, archived: false } }
      }

      await skeem.mutate({
        skeem_users: {
          update,
        },
      })
      await events.message("skeem_users", user.id, "User returned to company")
      await api.updateCompanySubscription(company.id)
      await Promise.resolve(refetch())
      alerts.genericSuccess("User marked as has returned")
    } catch (e) {
      console.error(e)
      alerts.genericError("Error updating user")
    }
  }

  async function handleAssignOwner() {
    setIsActioning(true)
    const response = await getAssignOwnerConfirmation({})
    if (!response) {
      setIsActioning(false)
      return
    }

    try {
      const currentOwner = user.company.users.find(u => u.isOwner)
      if (currentOwner) {
        const updatedCurrentOwner = await skeem.mutate({
          skeem_users: {
            update: {
              id: currentOwner.id,
              isOwner: false,
              // Downgrade the current owner to manager so we can still boot them back later.
              isManager: true,
            },
          },
        })

        const ownerDiff = diffRecordsForUpdateEvent(currentOwner, updatedCurrentOwner, [
          "isOwner",
          "isManager",
        ])
        if (ownerDiff.isDifferent) {
          await events.updateRecord("skeem_users", currentOwner.id, ownerDiff.diff)
        }
      }

      const updatedUser = await skeem.mutate({
        skeem_users: {
          update: {
            id: user.id,
            isOwner: true,
          },
        },
      })

      const ownerDiff = diffRecordsForUpdateEvent(user, updatedUser, ["isOwner"])
      if (ownerDiff.isDifferent) {
        await events.updateRecord("skeem_users", user.id, ownerDiff.diff)
      }

      alerts.updateAlert("User")
      history.push(backPath)
    } catch (e) {
      console.error(e)
      alerts.genericError()
    } finally {
      setIsActioning(false)
    }
  }

  const handleLogin = useAsyncCallback(async () => {
    try {
      const response = await api.generateLoginTokenForUser({ userId: user.id })
      const currentToken = localStorage.getItem("X-SKEEM_AUTH_TOKEN")
      localStorage.setItem("OLD__X-SKEEM_AUTH_TOKEN", currentToken || "")
      localStorage.setItem("X-SKEEM_AUTH_TOKEN", response.token)
      window.location.href = "/"
    } catch (error) {
      console.error("ERROR", error)
    }
  })

  if (!me) return <LoadingFrame />

  const isSelf = user.id === skeem.currentSession.id
  const canManageCompany = me.isManager || me.isOwner || me.isAdmin
  const canAssignOwner = !isSelf && !user.isOwner && (me.isAdmin || me.isOwner)
  const isSsoUser = user.authMethod === "sso"

  return (
    <div>
      <Modal
        onRequestClose={() => onAssignOwnerResolve(false)}
        isOpen={showAssignOwnerDialog}
        width={400}
        render={() => (
          <React.Fragment>
            <TextBlock blockSpacing={0.5}>
              <H3>Are you sure you want to assign this user as the master system admin?</H3>
              <P>
                The current master system admin of this company will be downgraded to a system
                admin.
              </P>
            </TextBlock>
            <Spacer />
            <MultiButton
              buttons={[
                { children: "Confirm", onClick: () => onAssignOwnerResolve(true) },
                {
                  children: "Cancel",
                  onClick: () => onAssignOwnerResolve(false),
                  buttonColor: "#fff",
                },
              ]}
            />
          </React.Fragment>
        )}
      />

      <List size={32}>
        {!user.isAnonymous && !user.isInvited && !isPayrollUser(user) && (
          <div>
            <H3>Send password reset email</H3>
            {isSsoUser ? (
              <P>
                This user signs in with SSO and does not have a local password, so password reset
                email is not available.
              </P>
            ) : (
              <P>
                Send a password reset email to the user if they have lost access to their account.
              </P>
            )}
            <CGButton
              disabled={isActioning || user.isLeaver || isSsoUser}
              onClick={handleSendPasswordResetEmail}
            >
              Send
            </CGButton>
          </div>
        )}

        {!user.isAnonymous && user.isInvited && (
          <div>
            <H3>Invite Pending</H3>
            <P>
              An invite has been emailed to the user account email address with instructions on how
              to complete their sign up.
            </P>
            <CGButton
              disabled={isActioning || user.isLeaver}
              onClick={handleSendPasswordResetEmail}
            >
              Resend invite
            </CGButton>
          </div>
        )}

        {!user.isAnonymous && canAssignOwner && (
          <div>
            <H3>Assign as the master system administrator</H3>
            <P>
              Mark this user as the master system administrator. A company can only have one master
              system admin and the current master system admin will lose their privileges.
            </P>
            <CGButton disabled={isActioning || user.isLeaver} onClick={handleAssignOwner}>
              Assign as the master system administrator
            </CGButton>
          </div>
        )}

        {!user.isAnonymous && canManageCompany && !user.isLeaver && !isSelf && (
          <div>
            <H3>Mark the user as having left the company.</H3>
            <P>Marking this user as has left will disable certain functionality for the account.</P>
            <CGButton disabled={isActioning} onClick={toggleUserHasLeft}>
              Mark user as has left
            </CGButton>
          </div>
        )}

        {user.isLeaver && canManageCompany && !isSelf && (
          <div>
            <H3>Mark user has returned</H3>
            <P>This will mark their user account as has returned to the company.</P>
            <CGButton disabled={isActioning} onClick={toggleUserHasLeft}>
              User has returned
            </CGButton>
          </div>
        )}

        {(me.isAdmin || canManageCompany) && !user.isAnonymous && !user.isLeaver && (
          <div>
            <H3>Login as user</H3>
            <P>Temporarily log in as this user, see the system from their point of view.</P>
            <CGButton onClick={handleLogin} disabled={user.isLeaver || user.isAnonymous}>
              Login
            </CGButton>
          </div>
        )}
      </List>
    </div>
  )
}
